// 新聞頁面 - 手機版置底功能列
'use client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    setShowLoginModal,
    setShowComment,
    popState,
    setShowHintModal,
    setHintText,
} from '@/slices/popSlice';
import { userState } from '@/slices/userSlice';
import { newsState, setNewsSocialCount } from '@/slices/newsSlice';
import newsClient from '@/common/apiClient/newsClient';
import useScrollDirection from '@/hook/useScrollDirection';
import { setUrlQuery } from '@/slices/urlSlice';
import Image from 'next/image';
import NewsEmoji from './NewsEmoji'; // 表情符號
import NewsMobileShare from './NewsMobileShare'; // 手機版分享
import useIsIphonePWA from '@/hook/useCheckIphone';

const NewsBottomFunction = ({ newsId, emojis, onClickEmoji }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector(userState);
    const { showComment } = useSelector(popState);
    const { newsSocialCount } = useSelector(newsState);

    const scrollDirection = useScrollDirection();
    const isIphonePWA = useIsIphonePWA();

    const [showEmoji, setShowEmoji] = useState(false); // 顯示表情符號
    const [showShare, setShowShare] = useState(false); // 顯示分享

    // 顯示登入彈窗
    const onShowLogin = () => {
        if (!user?.uid) {
            dispatch(setShowLoginModal(true));
            return false;
        }

        return true;
    };

    // 點擊讚讚
    const onThumb = () => {
        dispatch(setShowComment(false));
        setShowShare(false);
        setShowEmoji((prev) => !prev);
    };

    // 收藏
    const collectArticle = async () => {
        try {
            const { message } = await newsClient.collectNews(newsId);
            if (message === 'success') {
                dispatch(
                    setHintText(
                        newsSocialCount?.isFavorite ? 'HINT_COLLECT_CANCEL' : 'HINT_COLLECT'
                    )
                );
                dispatch(setShowHintModal(true));
                dispatch(
                    setNewsSocialCount({
                        favorite:
                            newsSocialCount?.favorite + (newsSocialCount?.isFavorite ? -1 : 1),
                        isFavorite: newsSocialCount?.isFavorite === 1 ? 0 : 1,
                    })
                );
            }
        } catch (e) {
            console.error(e);
        }
    };

    // 收藏（需登入）
    const onCollect = async () => {
        if (onShowLogin()) {
            collectArticle();
            dispatch(setShowComment(false));
            setShowEmoji(false);
            setShowShare(false);
        }
    };

    // 點擊評論（需登入）
    const onClickComment = () => {
        if (onShowLogin()) {
            setShowEmoji(false);
            setShowShare(false);
            dispatch(setShowComment(!showComment));
        } else dispatch(setUrlQuery(`?scrollToComment=true`));
    };

    // 點擊分享
    const onClickShare = () => {
        setShowEmoji(false);
        dispatch(setShowComment(false));
        setShowShare((prev) => !prev);
    };

    return (
        <div
            className={`hidden fixed bottom-0 left-0 w-screen z-[5] ulg:block trans
        ${scrollDirection !== 'down' ? 'ulg:bottom-[-60px]' : ''}`}
        >
            <div
                className={`between-center relative z-[2] bg-[rgba(0,0,0,.7)] backdrop-blur-[10px] w-full px-[25px]
                [&>button]:flex [&>button]:center-center [&>button]:gap-x-[8px] text-btn1 text-black2 py-[12px] [&>span]:w-[1px] [&>span]:h-[10px] [&>span]:border-l-1
                [&>span]:border-black2 [&>span]:border-opacity-[.5] [&>button]:flex-grow usm:px-[10px] [&>span]:uxs:hidden ${
                    isIphonePWA ? 'pb-[16px]' : ''
                }`}
            >
                {/* 按讚 */}
                <button onClick={onThumb}>
                    <Image
                        width={32}
                        height={32}
                        src="/images/social/icon_grey_thumb.svg"
                        alt="icon"
                    />
                    <span>{t('THUMB')}</span>
                </button>

                <span />

                {/* 收藏 */}
                <button onClick={onCollect}>
                    <Image
                        width={32}
                        height={32}
                        src="/images/social/icon_grey_collect.svg"
                        alt="icon"
                    />
                    <span>{t('COLLECT')}</span>
                </button>

                <span />

                {/* 評論 */}
                <button onClick={onClickComment}>
                    <Image
                        width={32}
                        height={32}
                        src="/images/social/icon_grey_comment.svg"
                        alt="icon"
                    />
                    <span className="ml-[-2px]">{t('SAY')}</span>
                </button>

                <span />

                {/* 分享 */}
                <button onClick={onClickShare}>
                    <Image
                        width={32}
                        height={32}
                        src="/images/social/icon_grey_share.svg"
                        alt="icon"
                    />
                    <span className="ml-[-3px]">{t('SHARE')}</span>
                </button>
            </div>
            {/* 表情符號 */}
            <div
                className={`absolute left-0 w-full z-[1] trans border-t-1  border-t-black9 ${
                    showEmoji ? 'bottom-[56px]' : 'bottom-[-60px]'
                }`}
            >
                <NewsEmoji
                    t={t}
                    location="bottom"
                    emojis={emojis}
                    newsId={newsId}
                    onClickEmoji={onClickEmoji}
                />

                {/* 關閉用背景 */}
                {showEmoji && (
                    <div
                        className="w-screen h-screen z-[0] top-0 left-0 fixed"
                        onClick={() => setShowEmoji(false)}
                    />
                )}
            </div>

            {/* 社群分享 */}
            <NewsMobileShare setShowShare={setShowShare} showShare={showShare} newsId={newsId} />
        </div>
    );
};

export default NewsBottomFunction;
